// This file is auto-generated. Do not edit manually.

import { z } from "zod";
import { UserSchema } from "./userSchemas";
import { ResponseMessageSchema, StatusSchema } from "./utilitySchemas";

export const ReminderStatusEnum = z.enum(["TO_DO", "COMPLETED", "CANCELLED"]);
export type ReminderStatus = z.infer<typeof ReminderStatusEnum>;

export const ReminderTypeEnum = z.enum(["REQUEST", "ENTITY", "USER", "SYSTEM"]);
export type ReminderType = z.infer<typeof ReminderTypeEnum>;

const ReminderSortByEnumSchema = z.enum([
    "dueDate",
    "status",
    "type",
    "id",
    "name",
    "description",
    "createdBy",
]);

export type ReminderSortBy = z.infer<typeof ReminderSortByEnumSchema>;

export const RemindersSchema = z.object({
    id: z.string().uuid(),
    status: ReminderStatusEnum,
    type: ReminderTypeEnum,
    description: z.string().max(1000),
    dueDate: z.string().refine((date) => !isNaN(Date.parse(date)), {
        message: "Invalid date format",
    }),
    assigneeId: z.number().int().positive().nullable(),
    assignee: UserSchema.pick({ id: true, firstName: true, lastName: true,email:true}).extend({
        _count: z.object({
            assignedTasks: z.number(),
        }),
    }).nullable(),
    createdById: z.number().int().positive(),
    createdBy: UserSchema.pick({ id: true, firstName: true, lastName: true }),
    createdAt: z.string().refine((date) => !isNaN(Date.parse(date)), {
        message: "Invalid date format",
    }),
    updatedAt: z.string().refine((date) => !isNaN(Date.parse(date)), {
        message: "Invalid date format",
    }),
    commentId: z.string().uuid().nullable(),
    // normalized fields for frontend convenience
    evalionId: z.string(),
    scopeId: z.union([z.string(), z.number()]),
    isExternal: z.boolean(),
    name: z.string(),
    // optional related entities for frontend convenience, not guaranteed to be present in all responses
    request: z.object({ id: z.string(), type: z.string(), evalionId: z.string(), isExternal: z.boolean() }).nullable(),
    entity: z.object({ id: z.number(), name: z.string() }).nullable(),
    user: z.object({ id: z.number(), firstName: z.string(), lastName: z.string(), entityId: z.number() }).nullable(),
});

export type Reminder = z.infer<typeof RemindersSchema>;

export const UpdateReminderSchema = z.object({
    status: ReminderStatusEnum.optional(),
    assigneeId: z.number().int().positive().optional(),
    description: z.string().max(1000).optional(),
    dueDate: z
        .string()
        .refine((date) => !isNaN(Date.parse(date)), {
            message: "Invalid date format",
        })
        .optional(),
    type: ReminderTypeEnum.optional(),
    scopeId: z.union([z.string(), z.number()]).optional(),
    isExternal: z.boolean().optional(),
});

export type UpdateReminder = z.infer<typeof UpdateReminderSchema>;

export const RemindersQuerySchema = z.object({
    limit: z.coerce.number().int().positive().optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
    sortBy: ReminderSortByEnumSchema.optional(),
    sortOrder: z.enum(["asc", "desc"]).optional(),
    filter: z
        .object({
            reminderStatus: ReminderStatusEnum.optional(),
            reminderAssigneeId: z.string().optional(),
            appraiserId: z.string().optional(),
        })
        .optional(),
});

export type RemindersQuery = z.infer<typeof RemindersQuerySchema>;

export const RemindersResponseSchema = z.object({
    status: StatusSchema,
    message: ResponseMessageSchema,
    data: z.object({
        reminders: z.array(RemindersSchema),
    }),
});

export type RemindersResponse = z.infer<typeof RemindersResponseSchema>;

export const CreateReminderSchema = z.object({
    description: z.string().max(1000),
    dueDate: z.string().refine((date) => !isNaN(Date.parse(date)), {
        message: "Invalid date format",
    }),
    assigneeId: z.number().int().positive(),
    type: ReminderTypeEnum.optional(),
    scopeId: z.union([z.string(), z.number()]).optional(),
    isExternal: z.boolean().optional(),
}).superRefine((data, ctx) => {
    if (!data.type) return;

    if (data.type === "REQUEST") {
        const requestId = z.string().uuid().safeParse(data.scopeId);
        if (!requestId.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["scopeId"],
                message: "requestId is required for REQUEST reminders",
            });
        }
        return;
    }

    if (data.type === "ENTITY") {
        const entityId = z.coerce
            .number()
            .int()
            .positive()
            .safeParse(data.scopeId);
        if (!entityId.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["scopeId"],
                message: "entityId is required for ENTITY reminders",
            });
        }
        return;
    }

    if (data.type === "USER") {
        const userId = z.coerce
            .number()
            .int()
            .positive()
            .safeParse(data.scopeId);
        if (!userId.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["scopeId"],
                message: "userId is required for USER reminders",
            });
        }
    }
});
export type CreateReminder = z.infer<typeof CreateReminderSchema>;

export const ReminderResponseSchema = z.object({
    status: StatusSchema,
    message: ResponseMessageSchema,
    data: z.object({
        reminder: RemindersSchema,
    }),
});

export type ReminderResponse = z.infer<typeof ReminderResponseSchema>;

const ReminderScopeOptionQuerySchema = z.object({
     q: z.string().optional(), 
     type: ReminderTypeEnum.optional(),
     appraiserId: z.string().optional(),
     limit: z.coerce.number().int().positive().optional(),
});

export type ReminderScopeOptionQuery = z.infer<typeof ReminderScopeOptionQuerySchema>;

const ReminderScopeOptionSchema = z.object({
    id: z.union([z.string(), z.number()]),
    label: z.string(),
    subLabel: z.string(),
    type: ReminderTypeEnum,
    isExternal: z.boolean().optional(),
});

export const ReminderScopeOptionsResponseSchema = z.object({
    status: StatusSchema,
    message: ResponseMessageSchema,
    data: z.object({
        options: z.array(ReminderScopeOptionSchema),
    }),
});

export type ReminderScopeOption = z.infer<typeof ReminderScopeOptionSchema>;

export const TaskReminderEmailTypeSchema = z.enum([
    "newTaskReminderAssigneeNotificationEmail",
]);

export type TaskReminderEmailType = z.infer<typeof TaskReminderEmailTypeSchema>;

export const SendTaskReminderEmailSchema = z.object({
    emailType: TaskReminderEmailTypeSchema,
    type: ReminderTypeEnum,
    scopeId: z.union([z.string(), z.number()]),
    isExternal: z.boolean(),
});

export type SendTaskReminderEmail = z.infer<typeof SendTaskReminderEmailSchema>;